import hyperdom from 'hyperdom'
import get from 'lodash/get'
import formatPrice from '../../../packages/ui-helpers/formatPrice.js'
import clone from 'root-core/utils/object/clone.js'
import placeholderPolyfill from '../../../placeholderPolyfill.jsx'
import * as validation from '../../../validation.js'
import paymentTabSchema from './paymentTabSchema.js'
import scroller from '../../../scroller.js'
import tripNotes from '../../../tripNotes.jsx'
import roundMoney from 'root-packages/price-engine/roundMoney.js'
import formatDate from 'root-packages/dates/formatDate.js'
import formats from 'root-packages/dates/formats.js'
import qs from 'qs'
import createPaypalButton from './paypalButton.jsx'
import createUplift from './upLift.jsx'
import {Message, MessageTypes} from '../../../message.jsx'
import PaymentOptions from './paymentOptions.js'
import PayMonthlyOption from './options/payMonthly.jsx'
import ZipMoney from './methods/zipMoney.jsx'
import format from 'root-core/utils/text/stringFormatFilter.js'

export default class PaymentTab {
  constructor (options) {
    this.features = options.config.features
    this.countries = options.countries
    this.datadog = options.datadog
    this.departure = options.departure
    this.config = options.config
    this.user = options.user
    this.availablePaymentMethods = options.availablePaymentMethods
    this.payment = {}
    this.supportedCardTypes = []
    this.paymentSuccessful = options.paymentSuccessful
    this.useLeadPassengerAddressForBilling = true
    this.api = options.api
    this.modals = options.modals
    this.discounts = options.discounts
    this.anyDiscounts = options.anyDiscounts
    this.totalBeforeDiscount = options.totalBeforeDiscount
    this.total = options.total
    this.schema = {}
    this.quoteCriteria = options.quoteCriteria
    this.booking = options.booking
    this.switchToTab = options.switchToTab
    this.showLoading = options.showLoading
    this.hideLoading = options.hideLoading
    this.serverErrorsPanel = options.serverErrorsPanel
    this.priceBreakdown = options.priceBreakdown
    this.paypalButton = this.features.paymentGatewayEnabled || createPaypalButton(this.config)
    this.browserCapabilities = options.browserCapabilities
    this.renderProceedWithBooking = true
    this.upliftRejected = false
    this.upliftReady = false
    this.isPlacedOnHold = false
    this.listenForPaymentServicesPostMessages()
    this.autoCancelBookingTimeoutModal = options.config.autoCancelBookingTimeoutModal
    this.autoCancelBookingCountdown = options.config.autoCancelBookingTimeoutModal
    this.time = {
      minutes: Math.floor(this.autoCancelBookingTimeoutModal / 60),
      seconds: this.autoCancelBookingTimeoutModal % 60,
    }
    this.itineraryImage = options.itineraryImage
  }

  getRemainingTimeToBook () {
    return `${this.autoCancelBookingTimeoutModal / 60} minutes`
  }

  displayAutoCancellationTime (autoCancelBookingTimeoutModal) {
    let minutes = Math.floor(autoCancelBookingTimeoutModal / 60)
    let seconds = autoCancelBookingTimeoutModal % 60
    return {
      minutes,
      seconds,
    }
  }

  formatTime (time) {
    let {minutes, seconds} = time
    return {
      minutes: String(minutes).padStart(2, '0'),
      seconds: String(seconds).padStart(2, '0'),
    }
  }

  shouldLoadUpLift () {
    return this.config.features.upliftEnabled && this.config.upliftApiKey && this.config.upliftCode
  }

  shouldLoadZipMoney () {
    return this.config.features.zipMoney
  }

  listenForPaymentServicesPostMessages () {
    if (window._paymentServicesPostMessageHandler) {
      window.removeEventListener('message', window._paymentServicesPostMessageHandler, false)
    }
    var postMessageHandler = (event) => this.handleWindowPostMessage(event)
    window._paymentServicesPostMessageHandler = postMessageHandler
    window.addEventListener('message', postMessageHandler, false)
  }

  interpretResponse (data = {}) {
    if (typeof data === 'string'){
      if (data.match(/^\[iFrameSizer\]/)) {
        return {}
      } else {
        return JSON.parse(data)
      }
    }
    return data
  }

  handleWindowPostMessage (event){
    var origin = event.origin || event.originalEvent.origin

    if (origin === this.config.paymentServicesUrl.replace(/\/$/, '')) {
      this.paymentServicesResponseReceived(event)
    }

    if(origin === this.config.zipMoneyUrl.replace(/\/$/, '')) {
      this.zipMoneyResponseReceived(event)
    }
  }

  zipMoneyResponseReceived (event){
    const response = event.data

    if (response.status === 'approved') {
      this.paymentOption().success()
      this.booking.paidViaZipMoney({reference: response.reference})
      this.serverErrorsPanel.clear()
      this.switchToTab('confirmation')
    } else if (response.status == 'rejected') {
      this.paymentOption().declined()
    } else if (response.status == 'error') {
      this.paymentOption().failed()
    } else if (response.status === 'referred') {
      this.zipMoneyReferred({reference:response.reference})
    } else if (response.status === 'cancelled') {
      this.paymentOption().closed()
    }

    this.hideLoading()
    this.refresh()
  }

  async zipMoneyReferred (options) {
    if (this.paymentOptions.isOnHoldAvailable()) {
      this.paymentOption().success()
      await this.booking.zipMoneyReferralHold(options.reference)
      this.serverErrorsPanel.clear()
      this.switchToTab('confirmation')
    } else {
      this.paymentOption().referred(options.reference)
    }
  }

  paymentServicesResponseReceived (event) {
    const paymentServicesResponse = this.interpretResponse(event.data)

    if (this.features.paymentGatewayEnabled && !(this.paymentOption() instanceof PayMonthlyOption)) {
      return
    }
    if (paymentServicesResponse.event === 'customerHasClickedPayButton') {
      this.isPayButtonClicked = true
      this.showLoading()
      this.refresh()
    }
    if (paymentServicesResponse.paymentSuccessful === 'true') {
      const amount = Number(paymentServicesResponse.amountPaid.total)
      const fee = Number(paymentServicesResponse.amountPaid.fee || 0)

      this.paymentOption().success()
      this.booking.paidViaPaymentServices(amount, fee)
      this.serverErrorsPanel.clear()
      this.switchToTab('confirmation')
      this.hideLoading()
      this.refresh()
    } else if (paymentServicesResponse.paymentSuccessful === 'false') {
      this.paymentOption().failed()
      this.hideLoading()
      this.isPayButtonClicked = false
    }
  }

  navigable () {
    return !!this.booking.booking
  }

  useLeadPassengerAddressChanged () {
    this.payment.billingAddress = this.useLeadPassengerAddressForBilling ? clone(this.quoteCriteria.leadPassenger.address) : {country: this.config.country}
    this.payment.billingAddress._visited = []
    return this.billingAddress.countryChanged()
  }

  unloadTab () {
    if (this.autoCancelBookingInterval) {
      clearInterval(this.autoCancelBookingInterval)
      this.autoCancelBookingCountdown = this.config.autoCancelBookingTimeoutModal
    }
    this.booking.unload()
  }

  getBillingAddress () {
    return new BillingAddress(this)
  }

  async loadTab () {
    this.supportedCardTypes = this.booking.booking.creditCardTypes
    this.schemas = paymentTabSchema(this.config)
    this.schema = this.schemas.payment

    this.bookingType = this.user.role === 'travelAgent' ? 'TA' : 'CD'

    if (!this.billingAddress) {
      this.billingAddress = this.getBillingAddress()
    }

    this.showLoading()
    this.paymentOptions = new PaymentOptions({
      availablePaymentMethods: this.availablePaymentMethods,
      config: this.config,
      depositProminenceEnabled: this.config.features.mobileDepositProminenceEnabled,
      paymentTab: this,
      standardPriceStrikeThroughAllowed: this.config.features.standardPriceStrikeThroughAllowed,
    })

    const bookingInitialDate = new Date().getTime()
    this.autoCancelBookingInterval = setInterval(() => {
      const bookingCurrentDate = new Date()
      const autoCancelBookingDifference = (bookingCurrentDate.getTime() - bookingInitialDate) / 1000
      this.autoCancelBookingCountdown = this.autoCancelBookingTimeoutModal - Math.round(autoCancelBookingDifference)
      if (this.autoCancelBookingCountdown <= 0) {
        clearInterval(this.autoCancelBookingInterval)
        this.autoCancelBookingCountdown = 0
      }
      if (this.refresh) {
        this.refresh()
      }
    }, 1000)

    if (this.shouldLoadZipMoney()) {
      this.paymentOptions.addZipMoneyMonthlyOption()
    }

    if (this.shouldLoadUpLift() && this.bookingType === 'CD' && !this.upliftRejected) {
      delete this.upliftLoadedInTime
      const upliftCallbacks = {
        showProceedWithBooking: show => {
          this.renderProceedWithBooking = show
          if (this.refresh) {
            this.refresh()
          }
        },
        creditDenied: () => {
          this.upliftRejected = true
          if (this.refresh) {
            this.refresh()
          }
        },
      }

      const updateUpliftListener = (tripInfo, api) => {
        api.update(tripInfo, () => {
          this.hideLoading()
          if (this.refresh) {
            this.refresh()
          }
        })
      }

      const onUpliftUpdated = (upliftEligible) => {
        if (this.upliftLoadedInTime === false) return
        this.upliftLoadedInTime = true
        this.paymentOptions.toggleUplift(upliftEligible && this.uplift.getMonthlyAmount() > 0)
        this.hideLoading()
        if (this.refresh) {
          this.refresh()
        }
      }

      const onUpliftLoaded = (upliftEligible, {api}) => {
        const upliftContainer = document.getElementById('uplift-container')
        if (upliftContainer) {
          const iframe = upliftContainer.querySelector('[name=pay-monthly]')
          if (iframe) {
            iframe.removeEventListener('load', updateUpliftListener, {capture: true})
            iframe.addEventListener('load', () => updateUpliftListener(this.upliftTripInfo, api))
          }
        }
        onUpliftUpdated(upliftEligible)
        this.upliftReady = true
        if (this.refresh) {
          this.refresh()
        }
        this.hideLoading()
      }

      this.uplift = await createUplift(this.config, upliftCallbacks, this.browserCapabilities)

      this.upliftTripInfo = this.uplift.getTripInfo({
        booking: this.booking.booking,
        quoteCriteria: this.quoteCriteria,
        bookingType: this.bookingType,
      })

      if (this.upliftReady) {
        this.uplift.update({
          tripInfo: this.upliftTripInfo,
          onUpdate: onUpliftUpdated,
        })
        this.hideLoading()
        if (this.refresh) {
          this.refresh()
        }
      } else {
        this.uplift.load({
          tripInfo: this.upliftTripInfo,
          onLoad: onUpliftLoaded,
          upliftRejected: this.upliftRejected,
        })
        setTimeout(() => {
          if (this.upliftLoadedInTime !== undefined) return
          this.upliftLoadedInTime = false
          this.paymentOptions.toggleUplift(true)
          this.hideLoading()
        }, this.config.features.upliftTimeout || 5000)
      }
    } else {
      this.hideLoading()
      if (this.refresh) {
        this.refresh()
      }
    }
    this.datadog.info('Payment tab loaded!')
  }

  paymentOption () {
    return this.paymentOptions.selected
  }

  paymentMethod () {
    return get(this.paymentOptions.selected, 'paymentMethods.selected')
  }

  calculateFeeAmount () {
    var amount = this.paymentOption().amount()
    return this.paymentMethod().feeAmount(amount)
  }

  calculatePaymentAmount () {
    var amount = this.paymentOption().amount()
    var fees = this.calculateFeeAmount()

    return roundMoney(amount + fees)
  }

  fieldVisited (model, property) {
    if (!model._visited) {
      Object.defineProperty(model, '_visited', {value: {}})
    }
    model._visited[property] = true

    this.validate()
  }

  validate () {
    this.validation = validation.validate(this.payment, this.schema)
    this.paymentMethod().validate(this.validation)

    this.showValidationErrors = !this.validation.valid
    return this.validation
  }

  renderValidationError (model, property) {
    if (this.showValidationErrors || (model._visited && model._visited[property])) {
      var error = this.validation.error(model, property)
      return this.renderError(error)
    }
  }

  zipcodePlaceholder (address) {
    return address.country === 'United States Of America' ? 'Zip *' : 'Zip/Postal Code *'
  }

  renderError (error) {
    if (error) {
      var s = this.hasScrolledToError ? undefined : scroller.renderScroller()
      this.hasScrolledToError = true
      return <div class='o-form-item__warning'>
        { s }
        <span>{error}</span>
      </div>
    }
  }

  submit () {
    if (!this.validate().valid) {
      this.hasScrolledToError = false
      return
    }
    const bookPromise = this.paymentMethod().book()
    if (bookPromise instanceof Promise) {
      return bookPromise.then(() => {
        this.serverErrorsPanel.clear()
        this.switchToTab('confirmation')
      }, error => {
        this.paymentErrors = error.errors || [error]
        this.api.onError({ErrorMessages: error.errors || [error]})
      })
    }
  }

  capturePaymentError (error) {
    this.paymentErrors = error.ErrorMessages
  }

  renderPriceDiscrepancy () {
    return this.booking.booking.priceDiscrepancy
      ? <div class='price-changed-warning'>
        {new Message({
          header: 'The total price has changed since you started booking',
          message: `The new price is ${formatPrice({currency: this.departure.prices.currency, amount: this.booking.booking.totalAmount})}`,
          type: MessageTypes.WARNING,
          hideIcon: true,
        })}
      </div>
      : ''
  }

  canPayByDirectDeposit () {
    return this.booking.booking.paymentMethods.directDeposit
  }

  canPayByCreditCard () {
    return this.booking.booking.paymentMethods.creditCard
  }

  renderPaymentOptions () {
    return <div class='payment-options'>{this.paymentOptions.eligible}</div>
  }

  renderPaymentMethodSelection ({noDetails} = {}) {
    var paymentOption = this.paymentOption()
    if (paymentOption.paymentMethods.eligible.length > 1 || noDetails) {
      this.selectedPaymentMethod = paymentOption.paymentMethods.selected.displayName

      return <div class='payment-methods no-details'>
        {
          paymentOption.paymentMethods.eligible.map(method => {
            return <div className={['payment-methods__item', {'payment-methods__item--display-row': paymentOption.paymentMethods.eligible.length > 3}]}>
              <label className={['o-choice-item payment-methods__o-choice-item',{'-selected': paymentOption.paymentMethods.selected == method}]}>
                <input type='radio' class='o-choice-item__radio' name='paymentMethod' id={method.name}
                  value={method}
                  binding={[paymentOption.paymentMethods, 'selected']} />
                <div class='o-choice-item__content'>
                  {method.renderPaymentOption()}
                </div>
              </label>
            </div>
          })
        }
      </div>
    }
  }

  renderPaymentAmount () {
    return <p class='final-payment-summary__headline'>Your payment today will be <strong>{formatPrice({currency: this.departure.prices.currency, amount: this.calculatePaymentAmount()})}</strong></p>
  }

  renderTermsAndConditions () {
    const canShowTripNotes = this.departure.notes && this.departure.notes.length

    return <div class='o-form-item u-margin-top-medium conditions' onmouseleave={() => this.showTripNotes = false}>
      <div class='payment-terms-and-conditions'>
        <label for='termsAndConditions' class='o-choice-item -fullWidth'>
          <input type='checkbox' class='o-choice-item__checkbox' name='termsAndConditions' id='termsAndConditions'
            binding={[this.payment, 'termsAndConditions']} />
          <div class='o-choice-item__content'>
            { this.config.isCanadian
              ? <span>I have read and agree to the {canShowTripNotes?<span><a href='' onclick={(ev) => { ev.preventDefault(); this.showTripNotes = true }} class='trip-notes-open'>trip notes</a>,</span>:null} <a href={this.config.brandPages.termsAndConditions} class='terms-and-conditions' target='_blank'>terms & conditions</a> and <a href={this.config.brandPages.privacyPolicy} class='privacy-policy' target='_blank'>privacy policy</a>.</span>
              : <span>I have read and agree to the {canShowTripNotes?<span><a href='' onclick={(ev) => { ev.preventDefault(); this.showTripNotes = true }} class='trip-notes-open'>trip notes</a> and</span>:null} <a href={this.config.brandPages.termsAndConditions} class='terms-and-conditions' target='_blank'>terms & conditions</a>.</span>
            }
          </div>
        </label>

        { this.renderValidationError(this.payment, 'termsAndConditions') }

      </div>
      { canShowTripNotes ?
        <div class={['help-content', {'trip-notes': this.showTripNotes}]}>
          { tripNotes(this.departure.notes) }
        </div>
        :null
      }
    </div>
  }

  renderBookingTimeoutModal () {
    if (this.autoCancelBookingCountdown > 0) {
      return
    }

    const {pathname, search} = window.location
    const queryString = qs.parse(search.slice(1))
    queryString.tab = undefined
    const queryStringWithoutTab = qs.stringify(queryString, {encode: true})
    return <div id='loadingBlock' class='o-centered-modal-wrapper'>
      <div class='o-centered-modal booking-timeout-container'>
        <div class="booking-timeout-container__header">Your booking has timed out</div>
        <div class="booking-timeout-container__text">{this.config.timeoutModal.description}</div>
        <div class="booking-timeout-container__buttons">
          <a href={`${pathname}?${queryStringWithoutTab}`} class='booking-timeout-container__button reversed-colors'>{this.config.timeoutModal.restartBookingBtn}</a>
          <a href={this.config.brandPages.brandWebsite} class='booking-timeout-container__button'>{format(this.config.timeoutModal.backHomeBtn, this.config.brandFamilyDisplayText)}</a>
        </div>
      </div>
    </div>
  }

  onload () {
    this.pageLoaded = true
    return this.useLeadPassengerAddressChanged()
  }

  renderPaymentInfoMessage () {
    if (!this.config.features.paymentInfoNotification.enabled) return

    return <div className='payment-note'>
      {hyperdom.rawHtml('', this.config.features.paymentInfoNotification.content)}
    </div>
  }

  render () {
    if (this.showValidationErrors) {
      this.validate()
    }
    const paymentMethod = this.paymentMethod()
    const isZipMoneyMethodSelected = paymentMethod instanceof ZipMoney

    return <div data-test={'payment-tab'} class='payments'>
      <form onsubmit={(ev) => {
        ev.preventDefault()
        return this.submit()
      }}
      focusable-form name='paymentForm' id='paymentForm' class='u-clearfix' novalidate>
        { this.serverErrorsPanel }
        { this.renderBookingTimeoutModal() }
        { this.renderPriceDiscrepancy() }
        { this.renderAutoCancellationMessage() }
        { this.renderPaymentInfoMessage() }
        { this.renderPaymentOptions() }
        { isZipMoneyMethodSelected ? <div>{ paymentMethod }</div> : undefined }
        { paymentMethod ? paymentMethod.renderReassurance() : undefined }
        { this.renderDepositNoticeForEPD() }
      </form>
    </div>
  }

  isPayPalEligible () {
    return this.config.features.paypalEnabled && !this.user.isTravelAgent
  }

  appliedEPDAmount () {
    return this.priceBreakdown().discounts.promotions.applied
      .filter(promo => promo.epd)
      .reduce((sum, promo) => sum + promo.amount, 0)
  }

  renderAutoCancellationMessage () {
    const time = this.formatTime(this.displayAutoCancellationTime(this.autoCancelBookingCountdown))
    return (
      <div class='message-info'>
        <h2>Time left to book: {time.minutes}:{time.seconds}</h2>
        <p>Complete your booking in the next {this.getRemainingTimeToBook()} to secure this price for your trip</p>
      </div>
    )
  }

  renderDepositNoticeForEPD () {
    const epdAmount = this.appliedEPDAmount()
    if (!this.config.features.epdDepositNotice || !this.booking.booking.paymentOptions.deposit || !epdAmount) {
      return
    }

    const {total, currency} = this.priceBreakdown()
    const depositAmount = get(this.booking, 'booking.paymentOptions.deposit.tempDesiredPaymentAmount', 0)
    const dueDate = formatDate(this.booking.booking.paymentOptions.full.dueDate, formats.dayMonthYear)
    const alternativeAmount = formatPrice({amount: (total  + epdAmount) - depositAmount, currency})

    return <p className="payments__epd-deposit-notice u-small-text u-margin-top-medium">
      {[
        `* - If choosing to book with deposit, you must pay the remaining amount before ${dueDate}. `,
        `Alternatively you will be charged ${alternativeAmount}`,
      ]}
    </p>
  }
}

class BillingAddress {
  constructor (payment) {
    this.payment = payment
    this.browserCapabilities = payment.browserCapabilities
    this.leadPassengerBinding = {
      get: () => this.useLeadPassengerAddressForBilling,
      set: (value) => {
        this.useLeadPassengerAddressForBilling = value
        this.billingAddress = this.useLeadPassengerAddressForBilling ? this.leadPassengerBillingAddress : this.nonLeadPassengerBillingAddress
        this.billingAddress._visited = []
        return this.countryChanged()
      },
    }

    this.leadPassengerBillingAddress = clone(this.payment.quoteCriteria.leadPassenger.address)
    this.nonLeadPassengerBillingAddress = {country: this.payment.config.country}
    this.leadPassengerBinding.set(true)
    this.schema = this.payment.schemas.billingAddress
  }

  countryChanged () {
    return this.payment.api.regions(this.billingAddress.country).then(regions => {
      this.regions = regions
    })
  }

  validate (results) {
    validation.validate(this.billingAddress, this.schema, results)
  }

  render () {
    return <div>
      <div class='o-inner-accent-box u-margin-bottom-small'>
        <label class='o-choice-item -fullWidth billing-address-is-leadpassenger-address'>
          <input type='checkbox' class='o-choice-item__checkbox' binding={this.leadPassengerBinding} />
          <div class='o-choice-item__content'>
            Billing address is the same as the lead passenger address
          </div>
        </label>
      </div>

      <div class='billing-address'>
        <h3>Billing Address</h3>
        <div class='o-form-item form-item-address-line1'>
          <label for='billing-address-line1' class='u-visually-hidden'>Street Address 1</label>

          {placeholderPolyfill(
            <input id='billing-address-line1' type='text' class='o-form-item__txt-input' placeholder='Street address 1 *'
              binding={[this.billingAddress, 'line1']}
              onblur={() => this.payment.fieldVisited(this.billingAddress, 'line1')}
              name='addressLine1' />,
            this.browserCapabilities)}

          { this.payment.renderValidationError(this.billingAddress, 'line1') }
        </div>

        <div class='o-form-item form-item-address-line2'>
          <label for='billing-address-line2' class='u-visually-hidden'>Street Address 2</label>
          {placeholderPolyfill(
            <input id='billing-address-line2' type='text' class='o-form-item__txt-input' placeholder='Street address 2'
              binding={[this.billingAddress, 'line2']}
              onblur={() => this.payment.fieldVisited(this.billingAddress, 'line2')}
              name='addressLine2' />,
            this.browserCapabilities)}

          { this.payment.renderValidationError(this.billingAddress, 'line2') }
        </div>

        <div class='o-form-item form-item-address-city'>
          <label for='billing-city' class='u-visually-hidden' />
          {placeholderPolyfill(
            <input id='billing-city' type='text' class='o-form-item__txt-input' placeholder='City *'
              binding={[this.billingAddress, 'city']}
              onblur={() => this.payment.fieldVisited(this.billingAddress, 'city')}
              name='city' />,
            this.browserCapabilities)}

          { this.payment.renderValidationError(this.billingAddress, 'city') }
        </div>

        <div class='o-form-item form-item-country'>
          <label for='billing-country' class='u-visually-hidden'>Country</label>
          <select id='billing-country' class='o-form-item__txt-input select-with-placeholder' name='country'
            binding={[this.billingAddress, 'country', () => this.countryChanged()]}
            onblur={() => this.payment.fieldVisited(this.billingAddress, 'country')}>
            {
              this.billingAddress.country ? <option disabled hidden value=''>Country</option> : <option value=''>Country</option>
            }
            <option value=''>Select Country *</option>
            {
              Object.keys(this.payment.countries()).map((countryCode) => {
                var country = this.payment.countries()[countryCode]
                return <option value={country.name}>{country.name}</option>
              })
            }
          </select>

          { this.payment.renderValidationError(this.billingAddress, 'country') }
        </div>

        <div class='o-form-item form-item-region'>
          <label for='billing-region' class='u-visually-hidden'>Region</label>
          {
            this.regions
              ? this.regions.length > 0 ? <select id='billing-region' class='o-form-item__txt-input select-with-placeholder'
                binding={[this.billingAddress, 'region']}
                onblur={() => this.payment.fieldVisited(this.billingAddress, 'region')}
                name='region' >
                <option value=''>Select Region *</option>
                {
                  this.regions.map((region) => {
                    return <option value={region}>{region}</option>
                  })
                }
              </select>
                : <input id='billing-region' class='o-form-item__txt-input' type='text' placeholder='Region *' name='region' binding={[this.billingAddress, 'region']} />
              : undefined
          }
          { this.payment.renderValidationError(this.billingAddress, 'region') }
        </div>

        <div class='o-form-item u-inbl form-item-postcode'>
          <label for='billing-postcode' class='u-visually-hidden' />
          {placeholderPolyfill(
            <input id='billing-postcode' type='text' class='o-form-item__txt-input -auto-width half-width'
              placeholder={this.payment.zipcodePlaceholder(this.billingAddress)}
              name='postCode'
              binding={[this.billingAddress, 'postcode']}
              onblur={() => this.payment.fieldVisited(this.billingAddress, 'postcode')} />,
            this.browserCapabilities
          )}

          { this.payment.renderValidationError(this.billingAddress, 'postcode') }
        </div>
      </div>
    </div>
  }
}
